import React, { Fragment } from 'react';

import { createStyles, Theme, withStyles } from '@material-ui/core/styles';
import { RadioGroup, FormControlLabel, Typography, Radio, FormControl, Input, InputAdornment, Select, MenuItem, Paper } from '@material-ui/core';
import MaterialTable from 'material-table';
import { MonetizationOn } from '@material-ui/icons';
import RadioButtonCheckedIcon from '@material-ui/icons/RadioButtonChecked';

import tableIcons from '../../../utils/editTableIcons';
import { formatCurrency } from '../../../utils/currencyFormatter';
import validation from '../../../utils/validation';

import ForwarderAccountAndConfig, { InvoiceAccountType } from '../../../models/ForwarderAccountAndConfig';
import ForwarderAccountTable from '../../../models/ForwarderAccountTable';

import style from './fwdAccounts.module.css';
import commonStyles from '../../common/common.module.css';

const iconFontSize = 20;

const styles = (theme: Theme) =>

  createStyles({
    materialIcon: {
      color: 'rgba(0, 50, 97 )',
      fontSize: iconFontSize,
      '&:hover': {
        cursor: 'pointer'
      }
    },
    radioGroup: {
      display: 'flex',
      flexDirection: 'row',
      minWidth: '150px',
      justifyContent: 'space-between'
    },
    spanOtherPayment: {
      display: 'flex',
      alignItems: 'center'
    },
    green: {
      color: 'green !important',
    },
    red: {
      color: 'red !important',
    },
    marginRight: {
      marginRight: '10px'
    },
    grey: {
      color: 'rgba(0,0, 0, 0.5)'
    },
    wrapper: {
      paddingTop: '25px',
    },
  });

interface FwdAccountsTableProps {
  classes: any;
  fwd: Array<ForwarderAccountTable>;
  changeFwdConfig:(data: ForwarderAccountAndConfig, doUpdate: boolean, fn: Function) => void
  openAccountBalanceDialog: (rowData: ForwarderAccountAndConfig) => void;
};

const FwdAccountsTable = (props: FwdAccountsTableProps) => {

  const { classes } = props;

  const editOtherPayments = (editComponentProps: any): JSX.Element => {
    return (
      <FormControl>
        <RadioGroup value={ editComponentProps.value } onChange={(e: any) => editComponentProps.onChange(e.target.value) } className={ classes.radioGroup }>
          <FormControlLabel  
            labelPlacement="end" 
            label={ <Typography className={ classes.green }>Yes</Typography> }
            className={ classes.green }
            value={ 'yes' }
            control={
              <Radio
                checked={ editComponentProps.value === 'yes' }
                checkedIcon={ <RadioButtonCheckedIcon className={ classes.green } /> }
                className={ classes.green }
              />
            }
          />
          <FormControlLabel  
            labelPlacement="end" 
            label={ <Typography className={ classes.red }>No</Typography> }
            className={ classes.red }
            value={ 'no' }
            control={
              <Radio
              checked={ editComponentProps.value === 'no' }
                checkedIcon={ <RadioButtonCheckedIcon className={ classes.red } /> }
                className={ classes.red }
              />
            }
          />
        </RadioGroup>
      </FormControl>
    )
  };

  const renderOtherPayments = (rowData: ForwarderAccountAndConfig): JSX.Element => {
    return rowData.other_payment_allowed === 'yes' ?
    <span className={ [classes.green, classes.spanOtherPayment].join(' ') }> <RadioButtonCheckedIcon className={ [classes.green, classes.marginRight].join(' ') } /> Yes</span>: 
    <span className={ [classes.red, classes.spanOtherPayment].join(' ') }> <RadioButtonCheckedIcon className={ [classes.red, classes.marginRight].join(' ') } /> No</span>
  };

  const forwarderAccountAndConfig = props.fwd.map((item: ForwarderAccountTable): ForwarderAccountAndConfig => {
    return {
      id: item.forwarder.id,
      name: item.forwarder.name,
      other_payment_allowed: item.config.other_payment_allowed,
      type: item.config.type,
      convenience_fee_usd: item.config.convenience_fee_usd,
      accountBalance: item.accountBalance,
      fwdInfo: item.forwarder.info,
      pay_by_wallet: false
    }
  });

  return (
    <Fragment>
      <div className={ `${ commonStyles.wrapper } ${style.frdTable } ${ classes.wrapper }` }>
        <h3 className={ `${ commonStyles.header } ` } >  Forwarder Accounts</h3>
        <MaterialTable
          components={{Container: props => <Paper {...props} elevation={0} className={ commonStyles.paperTable }/>}}

          icons={ tableIcons }
          
          data={ forwarderAccountAndConfig }

          columns={[
            { title: 'Customer', field: 'name', cellStyle: { minWidth: '150px' }, customFilterAndSearch: (term, fwd) => fwd.name.toLowerCase().indexOf(term.toLowerCase()) !== -1,
              editComponent: (editComponentProps: any) => {return <span>{editComponentProps.value}</span>}
            },
            
            {
              title: 'Other Payments', field: 'other_payment_allowed', render: (rowData: ForwarderAccountAndConfig) => { 
                return renderOtherPayments(rowData)
              },
               editComponent: (editComponentProps: any) => { return editOtherPayments(editComponentProps)}, 
              filtering: false
            },
            {
              title: 'Account Type', field: 'type', lookup: { 'None': InvoiceAccountType.NONE, 'invoice': InvoiceAccountType.INVOICE, 'prepaid': InvoiceAccountType.PREPAID }, filtering: false,
              editComponent: (editComponentProps: any) => {

                return <Select 
                        value={ editComponentProps.value }
                        onChange={ event => editComponentProps.onChange(event.target.value) }
                        className={ classes.select }
                       >
                        {
                          [
                            <MenuItem key={ 0 } value={ 'None' } >{ InvoiceAccountType.NONE }</MenuItem>,
                            <MenuItem key={ 1 } value={ 'invoice' } >{ InvoiceAccountType.INVOICE }</MenuItem>,
                            <MenuItem key={ 2 } value={ 'prepaid' } >{ InvoiceAccountType.PREPAID }</MenuItem>
                          ]
                        }
                       </Select>;
              }
            },
            {
              title: 'Convenience Fee', field: 'convenience_fee_usd', filtering: false,
              render: (rowData: ForwarderAccountAndConfig) => {
                const classNameAB: string = rowData.type == InvoiceAccountType.NONE ? classes.grey : '';
                return <span className={ classNameAB }>{formatCurrency(+rowData.convenience_fee_usd, 2, '$')}</span> 
              },  
              editComponent: (editComponentProps: any) => {
                return (
                  <Input 
                    id='fee'
                    value={ editComponentProps.value ? editComponentProps.value : '' }
                    onChange={ e => {
                      const value = e.target.value;
                      const isMoneySum: boolean = validation.validateSetMoneySum(value);
                      if (isMoneySum || value === '') {
                        editComponentProps.onChange(value) 
                      }
                    }}
                    startAdornment={ <InputAdornment position="start">$</InputAdornment> }
                  />
                )
              }
            },
            {
              title: 'Account Balance', filtering: false,
              render: (rowData: ForwarderAccountAndConfig) => {
                const classNameAB: string = rowData.type == InvoiceAccountType.NONE ? classes.grey : '';
               return <span className={ classNameAB }>{formatCurrency(+rowData.accountBalance, 2, '$')}</span> 
              },
            },
          ]}

          editable={{
            onRowUpdate: (newData: any, oldData: any ) => {

							return new Promise<void>((resolve, reject) => {

                let updData: ForwarderAccountAndConfig = newData;
                let doUpdate: boolean = true;

                if (newData.type !== oldData.type && newData.accountBalance > 0) {
    
                  updData = oldData;
                  doUpdate = false;
                }

                setTimeout(() => {
                  props.changeFwdConfig(updData, doUpdate, () => resolve()); 
                  resolve();
                }, 1000);
              });
            }
          }}

					actions = {[
						(rowData: ForwarderAccountAndConfig) => {
              if (rowData.type !== InvoiceAccountType.NONE ) {
                return (
                  {
                    icon: () =>
                      <MonetizationOn className={ [classes.materialIcon, classes.marginTop1px ].join(' ') } />,
                    tooltip: 'Change Account Balance',
                    onClick: () => { props.openAccountBalanceDialog(rowData) }
                  }
                )
              } 
              else return ({
                icon: '',
                onClick: () => {},
                disabled: true 
              }) 
            }
          ]}
          
          options={{
            paging: false,
            pageSize: 10,
            headerStyle: {
              backgroundColor: 'var(--tbl_head_bkg)',
              color: 'var(--tbl_head_color)',
              zIndex: 2,
            },
            showTitle: false,
            pageSizeOptions: [15, 20],
            toolbar: false,
            sorting: false,
            filtering: true,
            actionsColumnIndex: -1
          }}
        />
      </div>
    </Fragment>
  )
}

export default withStyles(styles)(FwdAccountsTable);